import { finalize, filter, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import some from 'lodash-es/some';
import * as moment from 'moment';
import { combineLatest } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { VideoPortalUser } from '../auth/video-portal-user.model';
import { Severity } from '../flash-messages/flash-message.model';
import { FlashMessagesService } from '../flash-messages/flash-messages.service';
import { GpsLogDateGroup } from '../gps-logs/gps-log-date-group.model';
import { GpsLogDeviceGroup } from '../gps-logs/gps-log-device-group.model';
import { GpsLog } from '../gps-logs/gps-log.model';
import { GpsLogsResponse } from '../gps-logs/gps-logs-response.model';
import { GpsLogsService, RequestType } from '../gps-logs/gps-logs.service';
import { Site } from '../sites/site.model';
import { SitesService } from '../sites/sites.service';
import { GpsLogItem } from '../gps-logs/gps-log-item.model';

@Injectable()
export class SearchService {
    gpsLogDateGroups: GpsLogDateGroup[];
    gpsLogItems: GpsLogItem[];

    // See: https://stackoverflow.com/questions/15965166/what-is-the-maximum-length-of-latitude-and-longitude
    maxLatitude = 90;
    maxLongitude = 180;

    messageIds = {
        httpError: <string>null,
        noResults: <string>null
    };

    minLatitude = -90;
    minLongitude = -180;
    searching = false;
    site = <Site>null;
    sites = <Site[]>[];
    siteSelectEnabled = false;
    userInfo: VideoPortalUser | null = null;
    address: string;

    private gpsLogsResponse: GpsLogsResponse;
    private _latitude = <number>null;
    private _longitude = <number>null;

    constructor(private authService: AuthService,
        sitesService: SitesService,
        private flashMessagesService: FlashMessagesService,
        private gpsLogsService: GpsLogsService) {

        // Wait for both sites and the first valid user info emission
        combineLatest([
            sitesService.all(),
            // Use filter and take(1) to get the first non-null user info
            authService.userInfoSubject.pipe(filter(user => !!user), take(1))
        ]).subscribe(([sites, userInfo]) => {
            this.sites = sites;
            this.userInfo = userInfo; // this.userInfo is set here

            // Now safely perform role-based logic
            if (this.userInfo.isInRole('council')) {
                this.site = this.sites.find(f => f.siteNumber === this.userInfo.councilSiteNumber);
                this.siteSelectEnabled = false; // Ensure flag is set correctly
            } else {
                this.siteSelectEnabled = true;
                // Site remains null until selected by the user
                this.site = null;
            }
        }, error => {
            console.error("SearchService: Error fetching sites or initial user info:", error);
            this.flashMessagesService.addMessage(Severity.Danger, "Failed to load site or user data for search.");
        });
    }

    get latitude(): number {
        return this._latitude;
    }

    set latitude(value: number) {
        this._latitude = value;
    }

    get longitude(): number {
        return this._longitude;
    }

    set longitude(value: number) {
        this._longitude = value;
    }

    clearAll(): void {
        this.latitude = null;
        this.longitude = null;
        if (this.siteSelectEnabled) {
            this.site = null;
        }
        this.clearResults();
        this.clearWarnings();
    }

    clearResults(): void {
        this.gpsLogDateGroups = null;
        this.gpsLogItems = null;
        this.gpsLogsResponse = null;
    }

    clearWarnings(): void {
        this.flashMessagesService.clear(this.messageIds.httpError);
        this.flashMessagesService.clear(this.messageIds.noResults);
    }

    isValid(): boolean {
        return !!(this.latitude
            && this.latitude >= this.minLatitude
            && this.latitude <= this.maxLatitude
            && this.longitude
            && this.longitude >= this.minLongitude
            && this.longitude <= this.maxLongitude
            && this.site
            && this.site.siteNumber
            && some(this.sites, (site: Site): boolean => (site && site.siteNumber) && (this.site.siteNumber === site.siteNumber)));
    }

    search(address: string, latitude: number, longitude: number): void {
        if (address !== null) {
            this.address = address;
        } else {
            this.latitude = latitude;
            this.longitude = longitude;
        }

        // Relying on GpsSearchComponent to wait for auth before calling search.
        // The constructor should have set this.userInfo by now.
        if (!this.searching && this.isValid()) {
            this.searching = true;
            this.clearResults();
            this.clearWarnings();
            // If this line fails, it means the component calling search didn't wait properly.
            const requestType = this.userInfo.isInRole('jjr') ? RequestType.Site : RequestType.Council;

            this.gpsLogsService.get(requestType, this.siteSelectEnabled ? this.site.siteNumber : '',
                this.latitude, this.longitude).pipe(finalize(() => {
                    if (address === null) {
                        this.latitude = latitude;
                        this.longitude = longitude;
                    }
                    this.searching = false;
                })).subscribe(gpsLogsResponse => {
                    if (gpsLogsResponse.filteredResultsCount === 0) {
                        this.messageIds.noResults = this.flashMessagesService.addMessage(
                            Severity.Warning, 'No search results found.');
                    } else {
                        this.gpsLogsResponse = gpsLogsResponse;
                        // Use a different loop variable name to avoid shadowing
                        this.gpsLogsResponse.gpsLogs.forEach(logEntry => this.constructResult(logEntry));
                    }
                }, error => {
                    this.messageIds.httpError = this.flashMessagesService.addMessageFor(error);
                });
        }
    }

    selectSite(site: Site): void {
        if (this.siteSelectEnabled) {
            this.site = site;
            this.clearWarnings();
            this.clearResults();
        }
    }

    // noinspection JSMethodCanBeStatic
    private compareDateOnly(d1: Date, d2: Date): boolean {
        return new Date(d1).setHours(0, 0, 0, 0) === new Date(d2).setHours(0, 0, 0, 0);
    }

    private constructResult(log: GpsLog): void {
        const item = new GpsLogItem(log);

        if (this.gpsLogItems == null) {
            this.gpsLogItems = [];
        }
        this.gpsLogItems.push(item);

        let dateGroup: GpsLogDateGroup;
        if (this.gpsLogDateGroups == null) {
            this.gpsLogDateGroups = [];
        } else {
            const date = item.timestamp.toDate();
            dateGroup = this.gpsLogDateGroups.find(r => this.compareDateOnly(r.date, date));
        }

        if (dateGroup == null) {
            dateGroup = new GpsLogDateGroup(item);
            this.gpsLogDateGroups.push(dateGroup);
        } else {
            let deviceGroup: GpsLogDeviceGroup;
            deviceGroup = dateGroup.devices.find(r => r.deviceId === log.deviceId);

            if (deviceGroup == null) {
                dateGroup.devices.push(new GpsLogDeviceGroup(item));
            } else {
                deviceGroup.items.push(item);
            }
        }
    }
}
